import { ChevronDown, ChevronRight } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useMemo, useState } from "react";
import {
  useAssignmentStore,
  useDisclosureStore,
  useEntryDataStore,
  useEntryUIStore,
} from "../../hooks/useDataEntryStore";
import { canEditDisclosure } from "../../domains/assignment";
import {
  filterOrgEntries,
  formatCarbonEmissionReduction,
  formatTotal,
  sortEntries,
} from "../../domains/dataEntry";
import { syncNxmapEntry } from "../../operations/entry.ops";
import CarbonEmissionReductionBadge from "./CarbonEmissionReductionBadge";
import EntryEmptyState from "./EntryEmptyState";
import EntryFilterBar from "./EntryFilterBar";
import EntryTable from "./EntryTable";
import OrgGroup from "./OrgGroup";
import TargetLabel from "./TargetLabel";
import PaginationControls from "@/components/common/PaginationControls";
import { useAuthStore } from "@/features/auth";
import { isDisclosureYearValueSelector } from "@/features/esg/utils/year-helpers";
import ReportingPeriodLabel from "../common/ReportingPeriodLabel";

const PAGE_SIZE = 50;

const toDateMs = (value) => {
  if (!value) return Number.NEGATIVE_INFINITY;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY;
};

const pickLatestEntry = (entries = []) =>
  entries.reduce((latest, current) => {
    if (!latest) return current;

    const currentReportingMs = toDateMs(current.reportingDate);
    const latestReportingMs = toDateMs(latest.reportingDate);
    if (currentReportingMs !== latestReportingMs) {
      return currentReportingMs > latestReportingMs ? current : latest;
    }

    const currentEntryMs = toDateMs(current.entryDate);
    const latestEntryMs = toDateMs(latest.entryDate);
    if (currentEntryMs !== latestEntryMs) {
      return currentEntryMs > latestEntryMs ? current : latest;
    }

    return current;
  }, null);

const formatLatestEntryTotal = (entries = [], fallbackUnit = null) => {
  const latest = pickLatestEntry(entries);
  const value = latest?.value;

  if (value == null || value === "") return null;

  const unit = latest?.unit || fallbackUnit || null;
  return unit ? `${value} ${unit}` : String(value);
};

const YearGroup = ({
  year,
  total,
  carbonEmissionReduction,
  entries,
  inputType,
  editable,
  disclosureId,
  orgId,
  onEdit,
  onDelete,
  onProof,
  onSync,
  permissions,
  reportingYears = [],
}) => {
  const [expanded, setExpanded] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [sort, setSort] = useState({ key: "reportingMonth", direction: "asc" });
  const Chevron = expanded ? ChevronDown : ChevronRight;

  const handleSort = (key) => {
    setSort((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" },
    );
    setPage(1);
  };

  const sorted = useMemo(() => sortEntries(entries, sort), [entries, sort]);
  const paginatedEntries = sorted.slice((page - 1) * pageSize, page * pageSize);

  const handlePageChange = (p) => setPage(p);
  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPage(1);
  };

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5">
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="flex items-center gap-1.5 font-semibold text-sm"
            onClick={() => setExpanded((v) => !v)}
          >
            <Chevron size={14} />
            <ReportingPeriodLabel
              reportingYears={reportingYears}
              year={year}
              direction="horizontal"
              //periodClassName="font-normal"
            />
            <span className="text-xs font-normal text-muted-foreground ml-1">
              ({entries.length})
            </span>
          </button>
          <TargetLabel
            disclosureId={disclosureId}
            organizationId={orgId}
            year={year}
          />
        </div>
        <div className="flex items-center gap-3">
          <CarbonEmissionReductionBadge value={carbonEmissionReduction} />
          {total ? (
            <span className="text-sm font-semibold text-primary">{total}</span>
          ) : !["selector", "simple", "simpleLongText"].includes(inputType) ? (
            <span className="text-sm font-semibold text-primary">N/A</span>
          ) : null}
        </div>
      </div>
      {expanded && (
        <div>
          <EntryTable
            entries={paginatedEntries}
            inputType={inputType}
            editable={editable}
            onEdit={onEdit}
            onDelete={onDelete}
            onProof={onProof}
            onSync={onSync}
            permissions={permissions}
            sort={sort}
            onSort={handleSort}
          />
          {entries.length > pageSize && (
            <div className="px-4 py-3 border-t border-border">
              <PaginationControls
                page={page}
                pageSize={pageSize}
                total={entries.length}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                pageSizeOptions={[30, 50, 100]}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const mapYearData = (
  disclosure,
  year,
  { totalValue, baseUnit, entries = [] },
  search,
) => {
  const filtered = search
    ? entries.filter((e) => e.label?.toLowerCase().includes(search))
    : entries;

  if (!filtered.length) return null;

  return {
    year: Number(year),
    total: isDisclosureYearValueSelector(disclosure)
      ? formatLatestEntryTotal(entries, baseUnit || disclosure?.defaultUnit)
      : formatTotal(totalValue, baseUnit),
    carbonEmissionReduction: formatCarbonEmissionReduction(
      totalValue,
      disclosure?.carbonEmissionMultiplier,
    ),
    entries: filtered,
  };
};

const StandardEntryView = observer(({ reportingYears = [] }) => {
  const { activeDisclosure } = useDisclosureStore();
  const { getAssignment } = useAssignmentStore();
  const { standardEntries, orgList, orgLookupMap } = useEntryDataStore();
  const {
    selectedOrg,
    selectedYear,
    searchEntry,
    setEditingEntry,
    setDeletingEntryId,
    setProofEntry,
  } = useEntryUIStore();
  const { user } = useAuthStore();
  const permissions = user?.permissions || [];

  const assignment = getAssignment(activeDisclosure?._id);
  const editable = canEditDisclosure(assignment);

  const handleEdit = (entry) => setEditingEntry(entry);
  const handleDelete = (entryId) => setDeletingEntryId(entryId);
  const handleProof = (entry) => setProofEntry(entry);
  const handleSync = (entryId) => syncNxmapEntry(entryId);

  const hasCreatePermission = permissions.includes("esg_data_entry:create");

  const inputType = activeDisclosure?.inputType || "value";

  const orgMap = useMemo(
    () => new Map(orgList.map((org) => [org.id, org])),
    [orgList],
  );

  const filteredOrgs = useMemo(
    () =>
      filterOrgEntries(
        standardEntries?.dataEntries,
        orgMap,
        { selectedOrg, selectedYear, searchEntry },
        (year, yearData, search) =>
          mapYearData(activeDisclosure, year, yearData, search),
        orgLookupMap,
      ),
    [
      activeDisclosure,
      standardEntries,
      orgMap,
      selectedOrg,
      selectedYear,
      searchEntry,
      orgLookupMap,
      orgLookupMap.size,
    ],
  );

  const hasEntries = Object.keys(standardEntries?.dataEntries || {}).length > 0;

  if (!hasEntries)
    return <EntryEmptyState hasCreatePermission={hasCreatePermission} />;

  return (
    <div className="space-y-4">
      <EntryFilterBar />

      <div className="divide-y-[1px] divide-border">
        {filteredOrgs.map((org) => (
          <OrgGroup key={org.orgId} {...org}>
            {org.years.map((yg) => (
              <YearGroup
                key={yg.year}
                {...yg}
                disclosureId={activeDisclosure?._id}
                orgId={org.orgId}
                inputType={inputType}
                editable={editable}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onProof={handleProof}
                onSync={handleSync}
                permissions={permissions}
                reportingYears={reportingYears}
              />
            ))}
          </OrgGroup>
        ))}
      </div>
    </div>
  );
});

export default StandardEntryView;
